<template>
  <v-container fluid class="production-details-page">
    <v-card v-if="production" id="printable-production" variant="outlined" class="production-document">
      <div class="document-heading pa-2 pa-md-3">
        <div class="text-center flex-grow-1">
          <div class="text-h6 font-weight-bold">{{ production.branch?.company?.name || 'Production' }}</div>
          <div class="text-subtitle-1 font-weight-medium text-decoration-underline mt-1">PRODUCTION REPORT</div>
          <div class="text-caption mt-1">{{ production.code }}</div>
        </div>
        <div class="heading-actions d-flex align-center ga-2 d-print-none">
          <v-chip :color="approvalColor(production.approval_status)" size="small" variant="tonal">
            {{ production.approval_status || (production.status ? 'Pending' : 'Draft') }}
          </v-chip>
          <v-btn icon="mdi-printer-outline" variant="tonal" color="primary" size="small" title="Print production report" @click="printPage" />
        </div>
      </div>

      <v-card-text class="pa-2 pa-md-3">
        <v-row dense class="mb-2">
          <v-col cols="6" md="3"><Info label="Production Date" :value="dateOnly(production.production_date)" /></v-col>
          <v-col cols="6" md="3"><Info label="Requisition" :value="production.requisition?.code || '-'" /></v-col>
          <v-col cols="6" md="3"><Info label="Sales Order / Purpose" :value="production.sale_order?.code || 'Stock Production'" /></v-col>
          <v-col cols="6" md="3"><Info label="Total Production Cost" :value="money(production.total_cost)" emphasis /></v-col>
          <v-col v-if="production.remarks" cols="12"><Info label="Remarks" :value="production.remarks" /></v-col>
        </v-row>

        <section v-for="detail in production.details || []" :key="detail.id" class="output-section mb-3">
          <div class="finished-product-header d-flex flex-wrap align-center ga-2 px-3 py-2">
            <div>
              <div class="text-caption text-medium-emphasis">Finished Product</div>
              <div class="text-subtitle-1 font-weight-bold">{{ detail.product?.code }} - {{ detail.product?.name }}</div>
              <div class="text-caption text-medium-emphasis">Template: {{ detail.template?.name || '-' }} · Version {{ detail.template?.version_no ?? '-' }}</div>
            </div>
            <v-spacer />
            <v-chip color="indigo" variant="tonal" size="small">Batch: {{ detail.batch_no || '-' }}</v-chip>
          </div>

          <div class="section-body">
            <div class="section-title mt-1">Batch &amp; Production Details</div>
            <v-row dense class="mb-2 mt-1">
              <v-col cols="6" sm="4" md="2"><Info label="Manufactured" :value="dateOnly(detail.manufacturing_date) || '-'" /></v-col>
              <v-col cols="6" sm="4" md="2"><Info label="Expiry" :value="dateOnly(detail.expiry_date) || '-'" /></v-col>
              <v-col cols="6" sm="4" md="2"><Info label="Days Remaining" :value="daysRemaining(detail.expiry_date)" /></v-col>
              <v-col cols="6" sm="4" md="2"><Info label="Planned Qty" :value="qty(detail.planned_quantity, detail)" /></v-col>
              <v-col cols="6" sm="4" md="2"><Info label="Actual FG Qty" :value="qty(detail.actual_quantity ?? detail.quantity, detail)" emphasis /></v-col>
              <v-col cols="6" sm="4" md="2"><Info label="Yield" :value="`${number(detail.yield_percent, 2)}%`" /></v-col>
              <v-col cols="6" sm="4" md="2"><Info label="FG Unit Cost" :value="money(detail.total_unit_cost, 6)" emphasis /></v-col>
            </v-row>

            <div class="section-title">Formulation — Raw Materials</div>
            <div class="table-wrap mt-1 mb-2">
              <table class="report-table formulation-table">
                <thead>
                  <tr>
                    <th>Raw Material</th>
                    <th class="num">Planned Qty</th>
                    <th class="num">Used Qty</th>
                    <th class="num">Std. Purity %</th>
                    <th class="num">Present Purity %</th>
                    <th class="num">Loss %</th>
                    <th class="num">Material Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="material in materialsByType(detail, 'raw_material')" :key="material.id">
                    <td class="font-weight-medium">{{ materialName(material) }}</td>
                    <td class="num">{{ materialQuantity(material, 'total_required_quantity') }}</td>
                    <td class="num">{{ materialQuantity(material, 'actual_quantity') }}</td>
                    <td class="num">{{ purityValue(standardPurity(detail, material)) }}</td>
                    <td class="num">{{ purityValue(requisitionMaterial(detail, material)?.present_purity) }}</td>
                    <td class="num">{{ number(material.process_loss_percent, 2) }}%</td>
                    <td class="num">{{ money(material.material_cost) }}</td>
                  </tr>
                  <tr v-if="!materialsByType(detail, 'raw_material').length"><td colspan="7" class="empty">No raw materials recorded.</td></tr>
                  <tr v-else class="total-row">
                    <td>Total RM</td>
                    <td class="num">{{ materialTotalQuantity(detail, 'raw_material', 'total_required_quantity') }}</td>
                    <td class="num">{{ materialTotalQuantity(detail, 'raw_material', 'actual_quantity') }}</td>
                    <td colspan="3"></td>
                    <td class="num">{{ money(detail.material_cost) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <details class="consumption-summary mb-2">
              <summary class="section-title consumption-summary-title">Required Materials — Actual Consumption <span class="text-caption text-medium-emphasis">(expand for RM/PM summary)</span></summary>
            <v-row dense class="mt-2 mb-2">
              <v-col cols="12" md="6">
                <div class="table-wrap h-100">
                  <table class="report-table summary-table">
                    <thead><tr><th>Raw Material (RM)</th><th class="num">Qty</th><th class="num">Cost</th></tr></thead>
                    <tbody>
                      <tr v-for="material in materialsByType(detail, 'raw_material')" :key="material.id">
                        <td>{{ materialName(material) }}</td>
                        <td class="num">{{ materialQuantity(material, 'actual_quantity') }}</td>
                        <td class="num">{{ money(material.material_cost) }}</td>
                      </tr>
                      <tr v-if="!materialsByType(detail, 'raw_material').length"><td colspan="3" class="empty">No raw material</td></tr>
                      <tr class="total-row"><td>Total RM</td><td class="num">{{ materialTotalQuantity(detail, 'raw_material', 'actual_quantity') }}</td><td class="num">{{ money(detail.material_cost) }}</td></tr>
                    </tbody>
                  </table>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="table-wrap h-100">
                  <table class="report-table summary-table">
                    <thead><tr><th>Packaging Material (PM)</th><th class="num">Qty</th><th class="num">Cost</th></tr></thead>
                    <tbody>
                      <tr v-for="material in materialsByType(detail, 'packaging')" :key="material.id">
                        <td>{{ materialName(material) }}</td>
                        <td class="num">{{ materialQuantity(material, 'actual_quantity') }}</td>
                        <td class="num">{{ money(material.material_cost) }}</td>
                      </tr>
                      <tr v-if="!materialsByType(detail, 'packaging').length"><td colspan="3" class="empty">No packaging material</td></tr>
                      <tr class="total-row"><td>Total PM</td><td class="num">{{ materialTotalQuantity(detail, 'packaging', 'actual_quantity') }}</td><td class="num">{{ money(detail.packaging_cost) }}</td></tr>
                    </tbody>
                  </table>
                </div>
              </v-col>
            </v-row>

            </details>
            <div class="section-title">Consumed Lots — Batch, Purity &amp; Expiry Trace</div>
            <div class="text-caption text-medium-emphasis mt-1">Days remaining = Expiry Date minus today's date. One row is shown per consumed lot.</div>
            <div class="table-wrap mt-1 mb-2">
              <table class="report-table trace-table">
                <thead><tr>
                  <th>Material</th><th>Type</th><th>Lot / Batch</th><th>Expiry</th>
                  <th class="num">Days Remaining</th><th class="num">Pr. Purity %</th>
                  <th class="num">Used Qty</th><th class="num">Unit Cost</th><th class="num">Amount</th>
                </tr></thead>
                <tbody>
                  <tr v-for="consumption in detail.material_consumptions || []" :key="consumption.id">
                    <td class="font-weight-medium">{{ consumedMaterialName(detail, consumption) }}</td>
                    <td><span class="type-chip" :class="consumedType(detail, consumption) === 'packaging' ? 'type-chip--pm' : 'type-chip--rm'">{{ consumedType(detail, consumption) === 'packaging' ? 'PM' : 'RM' }}</span></td>
                    <td>
                      <div>{{ consumption.delivery_allocation?.lot_no || '-' }}</div>
                      <div class="text-caption text-medium-emphasis">{{ consumption.delivery_allocation?.batch_no || '-' }}</div>
                    </td>
                    <td>{{ dateOnly(consumption.delivery_allocation?.expiry_date) || '-' }}</td>
                    <td class="num">{{ daysRemaining(consumption.delivery_allocation?.expiry_date) }}</td>
                    <td class="num">{{ consumedType(detail, consumption) === 'packaging' ? '-' : purityValue(consumption.purchase_purity) }}</td>
                    <td class="num">{{ number(consumption.quantity) }}</td>
                    <td class="num">{{ money(consumption.unit_cost, 6) }}</td>
                    <td class="num">{{ money(consumption.amount) }}</td>
                  </tr>
                  <tr v-if="!(detail.material_consumptions || []).length"><td colspan="9" class="empty">No consumed lot information found.</td></tr>
                </tbody>
              </table>
            </div>

            <div class="section-title">Production Cost Breakdown</div>
            <v-row dense class="mt-1 mb-2">
              <v-col cols="12" md="6">
                <div class="table-wrap">
                  <table class="report-table cost-table">
                    <tbody>
                      <tr><td>Raw Material Cost</td><td class="num">{{ money(detail.material_cost) }}</td></tr>
                      <tr><td>Packaging Material Cost</td><td class="num">{{ money(detail.packaging_cost) }}</td></tr>
                      <tr><td>Labour Cost</td><td class="num">{{ money(detail.labour_cost) }}</td></tr>
                      <tr><td>Overhead &amp; Other</td><td class="num">{{ money(detail.overhead_cost) }}</td></tr>
                      <tr class="total-row"><td>Actual Total Cost</td><td class="num">{{ money(detail.actual_total_cost) }}</td></tr>
                      <tr class="total-row"><td>Finished Goods Unit Cost</td><td class="num">{{ money(detail.total_unit_cost, 6) }}</td></tr>
                    </tbody>
                  </table>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="table-wrap">
                  <table class="report-table cost-table">
                    <thead><tr><th>Additional Cost</th><th>Description</th><th class="num">Amount</th></tr></thead>
                    <tbody>
                      <tr v-for="cost in detail.cost_details || []" :key="cost.id">
                        <td>{{ labelize(cost.cost_type) }}</td>
                        <td>{{ cost.description || '-' }}</td>
                        <td class="num">{{ money(cost.amount) }}</td>
                      </tr>
                      <tr v-if="!(detail.cost_details || []).length"><td colspan="3" class="empty">No additional cost.</td></tr>
                    </tbody>
                  </table>
                </div>
              </v-col>
            </v-row>

            <div class="signature-row">
              <div>Production Executive</div><div>QC Executive</div><div>Production Manager</div>
            </div>
          </div>
        </section>
      </v-card-text>
    </v-card>
    <v-skeleton-loader v-else type="article, table" />

    <!-- Dedicated print report, teleported outside the Vuetify app so navigation,
         fixed footers and scroll containers cannot split or clip print pages. -->
    <Teleport to="body">
      <div v-if="production" class="production-print-sheet">
        <article
          v-for="detail in production.details || []"
          :key="detail.id"
          class="print-product-block"
        >
          <header class="print-heading">
            <h1>{{ production.branch?.company?.name || 'Remex Ingredient Solution' }}</h1>
            <h2>PRODUCTION COMPLETION REPORT</h2>
            <div class="print-doc-code">{{ production.code }} <span>·</span> {{ dateOnly(production.production_date) }}</div>
          </header>

          <table class="print-meta">
            <tbody>
              <tr>
                <td><span>Requisition</span><strong>{{ production.requisition?.code || '-' }}</strong></td>
                <td><span>Sales Order / Purpose</span><strong>{{ production.sale_order?.code || 'Stock Production' }}</strong></td>
                <td><span>Production Date</span><strong>{{ dateOnly(production.production_date) }}</strong></td>
                <td><span>Production Cost</span><strong>{{ money(production.total_cost) }}</strong></td>
              </tr>
            </tbody>
          </table>

          <div class="print-product-title">
            <strong>{{ detail.product?.code }} - {{ detail.product?.name }}</strong>
            <span>Finished Batch: <b>{{ detail.batch_no || '-' }}</b></span>
          </div>
          <table class="print-meta print-meta--batch">
            <tbody>
              <tr>
                <td><span>Manufactured</span><strong>{{ dateOnly(detail.manufacturing_date) || '-' }}</strong></td>
                <td><span>Expiry</span><strong>{{ dateOnly(detail.expiry_date) || '-' }}</strong></td>
                <td><span>Days Remaining</span><strong>{{ daysRemaining(detail.expiry_date) }}</strong></td>
                <td><span>Planned</span><strong>{{ qty(detail.planned_quantity, detail) }}</strong></td>
                <td><span>Actual FG</span><strong>{{ qty(detail.actual_quantity ?? detail.quantity, detail) }}</strong></td>
                <td><span>Yield</span><strong>{{ number(detail.yield_percent, 2) }}%</strong></td>
                <td><span>Unit Cost</span><strong>{{ money(detail.total_unit_cost, 6) }}</strong></td>
              </tr>
            </tbody>
          </table>

          <h3 class="print-section-heading">FORMULATION — RAW MATERIALS</h3>
          <table class="print-table">
            <thead>
              <tr>
                <th>Raw Material</th>
                <th class="print-num">Std. Purity</th>
                <th class="print-num">Present Purity</th>
                <th class="print-num">Planned Qty</th>
                <th class="print-num">Used Qty</th>
                <th class="print-num">Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="material in materialsByType(detail, 'raw_material')" :key="material.id">
                <td>{{ materialName(material) }}</td>
                <td class="print-num">{{ purityValue(standardPurity(detail, material)) }}</td>
                <td class="print-num">{{ purityValue(requisitionMaterial(detail, material)?.present_purity) }}</td>
                <td class="print-num">{{ materialQuantity(material, 'total_required_quantity') }}</td>
                <td class="print-num">{{ materialQuantity(material, 'actual_quantity') }}</td>
                <td class="print-num">{{ money(material.material_cost) }}</td>
              </tr>
              <tr v-if="!materialsByType(detail, 'raw_material').length"><td colspan="6">No raw material records</td></tr>
              <tr v-else class="print-total-row">
                <td colspan="3">Total Raw Materials</td>
                <td class="print-num">{{ materialTotalQuantity(detail, 'raw_material', 'total_required_quantity') }}</td>
                <td class="print-num">{{ materialTotalQuantity(detail, 'raw_material', 'actual_quantity') }}</td>
                <td class="print-num">{{ money(detail.material_cost) }}</td>
              </tr>
            </tbody>
          </table>

          <h3 class="print-section-heading">PACKAGING MATERIALS</h3>
          <table class="print-table">
            <thead>
              <tr><th>Packaging Product</th><th class="print-num">Planned Qty</th><th class="print-num">Used Qty</th><th class="print-num">Cost</th></tr>
            </thead>
            <tbody>
              <tr v-for="material in materialsByType(detail, 'packaging')" :key="material.id">
                <td>{{ materialName(material) }}</td>
                <td class="print-num">{{ materialQuantity(material, 'total_required_quantity') }}</td>
                <td class="print-num">{{ materialQuantity(material, 'actual_quantity') }}</td>
                <td class="print-num">{{ money(material.material_cost) }}</td>
              </tr>
              <tr v-if="!materialsByType(detail, 'packaging').length"><td colspan="4">No packaging materials</td></tr>
              <tr v-else class="print-total-row">
                <td>Total Packaging</td>
                <td class="print-num">{{ materialTotalQuantity(detail, 'packaging', 'total_required_quantity') }}</td>
                <td class="print-num">{{ materialTotalQuantity(detail, 'packaging', 'actual_quantity') }}</td>
                <td class="print-num">{{ money(detail.packaging_cost) }}</td>
              </tr>
            </tbody>
          </table>

          <h3 class="print-section-heading">CONSUMED LOTS — TRACEABILITY</h3>
          <table class="print-table print-trace-table">
            <thead>
              <tr>
                <th>Material</th><th>Type</th><th>Lot / Batch</th><th>Expiry</th>
                <th class="print-num">Days Remaining</th><th class="print-num">Purchase Purity</th>
                <th class="print-num">Qty</th><th class="print-num">Unit Cost</th><th class="print-num">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="consumption in detail.material_consumptions || []" :key="consumption.id">
                <td>{{ consumedMaterialName(detail, consumption) }}</td>
                <td>{{ consumedType(detail, consumption) === 'packaging' ? 'PM' : 'RM' }}</td>
                <td>{{ consumption.delivery_allocation?.batch_no || consumption.delivery_allocation?.lot_no || '-' }}</td>
                <td>{{ dateOnly(consumption.delivery_allocation?.expiry_date) || '-' }}</td>
                <td class="print-num">{{ daysRemaining(consumption.delivery_allocation?.expiry_date) }}</td>
                <td class="print-num">{{ consumedType(detail, consumption) === 'packaging' ? '-' : purityValue(consumption.purchase_purity) }}</td>
                <td class="print-num">{{ number(consumption.quantity) }}</td>
                <td class="print-num">{{ money(consumption.unit_cost, 4) }}</td>
                <td class="print-num">{{ money(consumption.amount) }}</td>
              </tr>
              <tr v-if="!(detail.material_consumptions || []).length"><td colspan="9">No lot consumption records</td></tr>
            </tbody>
          </table>

          <h3 class="print-section-heading">PRODUCTION COSTING</h3>
          <table class="print-table print-cost-table">
            <thead><tr><th>Raw Material</th><th>Packaging</th><th>Labour</th><th>Overhead / Other</th><th>Actual Total</th><th>FG Unit Cost</th></tr></thead>
            <tbody><tr>
              <td>{{ money(detail.material_cost) }}</td><td>{{ money(detail.packaging_cost) }}</td>
              <td>{{ money(detail.labour_cost) }}</td><td>{{ money(detail.overhead_cost) }}</td>
              <td><b>{{ money(detail.actual_total_cost) }}</b></td><td><b>{{ money(detail.total_unit_cost, 6) }}</b></td>
            </tr></tbody>
          </table>

          <div v-if="production.remarks" class="print-remarks"><b>Remarks:</b> {{ production.remarks }}</div>
          <div class="print-signatures">
            <div>Production Executive</div><div>QC Executive</div><div>Production Manager</div>
          </div>
          <div class="print-footnote">Generated from Remex ERP · {{ production.code }} · {{ detail.product?.name }}</div>
        </article>
      </div>
    </Teleport>
  </v-container>
</template>

<script setup>
import { defineComponent, h, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { toast } from 'vue3-toastify';
import { openProductionPrintPreview } from './productionPrint';

const Info = defineComponent({
  props: { label: String, value: [String, Number], emphasis: Boolean },
  setup(props) {
    return () => h('div', { class: ['info-box', props.emphasis ? 'info-box--emphasis' : ''] }, [
      h('div', { class: 'info-label' }, props.label),
      h('div', { class: 'info-value' }, String(props.value ?? '-')),
    ]);
  },
});

const route = useRoute();
const store = useStore();
const production = ref(null);

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}
function money(value, digits = 2) { return number(value, digits); }
function dateOnly(value) { return value ? String(value).slice(0, 10) : ''; }
function labelize(value) {
  return String(value || '-').replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
function approvalColor(status) {
  if (status === 'Approved') return 'success';
  if (status === 'Rejected' || status === 'Cancelled') return 'error';
  return 'warning';
}
function qty(value, detail) {
  return `${number(value)} ${detail.product?.unit?.name || ''}`.trim();
}
function requisitionMaterial(detail, material) {
  return (detail.production_requisition_detail?.raw_product_details || []).find(
    (item) => Number(item.id) === Number(material.requisition_raw_detail_id)
  );
}
function materialType(detail, material) {
  return requisitionMaterial(detail, material)?.item_type || 'raw_material';
}
function materialsByType(detail, type) {
  return (detail.raw_product_details || []).filter((item) => (materialType(detail, item) === 'packaging') === (type === 'packaging'));
}
function materialName(material) {
  const code = material.raw_product?.code;
  return `${code ? `${code} - ` : ''}${material.raw_product?.name || material.raw_product_id || '-'}`;
}
function standardPurity(detail, material) {
  return requisitionMaterial(detail, material)?.standard_purity ?? material.standard_purity;
}
function purityValue(value) {
  return Number(value) > 0 ? `${number(value, 2)}%` : '-';
}
function materialTotal(detail, type, field) {
  return materialsByType(detail, type).reduce((sum, item) => sum + Number(item[field] || 0), 0);
}
function materialQuantity(material, field) {
  const unit = material.raw_product?.unit?.name || '';
  return `${number(material[field])}${unit ? ` ${unit}` : ''}`;
}
function materialTotalQuantity(detail, type, field) {
  const materials = materialsByType(detail, type);
  const unitNames = [...new Set(materials.map((item) => item.raw_product?.unit?.name || '').filter(Boolean))];
  if (unitNames.length > 1 || (unitNames.length && materials.some((item) => !item.raw_product?.unit?.name))) {
    return 'Mixed units';
  }
  return `${number(materialTotal(detail, type, field))}${unitNames.length ? ` ${unitNames[0]}` : ''}`;
}
function consumedMaterial(detail, consumption) {
  return (detail.raw_product_details || []).find(
    (item) => Number(item.requisition_raw_detail_id) === Number(consumption.requisition_raw_detail_id)
  );
}
function consumedMaterialName(detail, consumption) {
  const material = consumedMaterial(detail, consumption);
  return material ? materialName(material) : (consumption.product_id || '-');
}
function consumedType(detail, consumption) {
  const material = consumedMaterial(detail, consumption);
  return material ? materialType(detail, material) : 'raw_material';
}
function todayLocalISO() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
// Calendar-day difference, not an elapsed 24-hour interval (DST/timezone safe).
function daysRemaining(expiry) {
  const end = dateOnly(expiry);
  if (!end) return '-';
  const expiryMs = Date.parse(`${end}T00:00:00Z`);
  const todayMs = Date.parse(`${todayLocalISO()}T00:00:00Z`);
  if (!Number.isFinite(expiryMs) || !Number.isFinite(todayMs)) return '-';
  const days = Math.round((expiryMs - todayMs) / 86400000);
  if (days < 0) return `Expired (${Math.abs(days)} days)`;
  if (days === 0) return 'Expires today';
  return `${days} days`;
}
function printPage() {
  const sheet = document.querySelector('body > .production-print-sheet');
  if (!sheet || !production.value) {
    toast.error('Print report is not ready yet.');
    return;
  }
  const opened = openProductionPrintPreview(sheet.innerHTML, production.value.code);
  if (!opened) toast.error('Please allow pop-ups for this site to print the production report.');
}

onMounted(async () => {
  document.body.classList.add('remex-production-print-page');
  try {
    const response = await store.dispatch('getPrdProductionById', route.params.id);
    production.value = response?.data || response;
  } catch (error) {
    toast.error(error?.response?.data?.message || 'Production details could not be loaded.');
  }
});
onBeforeUnmount(() => {
  document.body.classList.remove('remex-production-print-page');
});
</script>

<style scoped>
.consumption-summary { border: 1px solid #dce3eb; border-radius: 6px; overflow: hidden; }
.consumption-summary-title { cursor: pointer; list-style-position: inside; user-select: none; }
.consumption-summary[open] > .consumption-summary-title { margin-bottom: 5px; }
.consumption-summary > .v-row { padding: 0 8px 6px; }
.production-document { overflow: hidden; border-radius: 10px; }
.document-heading { background: #eef3f6; border-bottom: 1px solid #d7e0e5; position: relative; display: flex; }
.heading-actions { position: absolute; right: 14px; top: 16px; }
.info-box { padding: 6px 9px; border: 1px solid #e1e5eb; border-radius: 8px; min-height: 51px; background: #fff; height: 100%; }
.info-box--emphasis { border-color: #a5c8e8; background: #f0f7ff; }
.info-label { color: #627185; font-size: .7rem; text-transform: uppercase; letter-spacing: .35px; }
.info-value { margin-top: 3px; color: #1b2838; font-weight: 700; font-size: .91rem; overflow-wrap: anywhere; }
.output-section { border: 1px solid #dfe4ec; border-radius: 9px; overflow: hidden; }
.finished-product-header { background: #e8eff5; border-bottom: 1px solid #d8e0e8; }
.section-body { padding: 10px 12px; }
.section-title { border-left: 3px solid #526a82; background: #f3f6f8; padding: 5px 9px; font-weight: 700; font-size: .83rem; }
.table-wrap { overflow-x: auto; border: 1px solid #d9dfe5; border-radius: 5px; }
.report-table { border-collapse: collapse; min-width: 650px; width: 100%; font-size: .79rem; }
.report-table th { font-weight: 700; background: #e8edf1; text-align: left; white-space: nowrap; }
.report-table th, .report-table td { padding: 5px 7px; border-bottom: 1px solid #e0e4e8; vertical-align: middle; }
.report-table tbody tr:nth-child(even) { background: #fafbfc; }
.report-table tbody tr:last-child td { border-bottom: 0; }
.report-table .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.report-table .empty { text-align: center; color: #778493; padding: 14px; }
.report-table .total-row { background: #eaf1f5 !important; font-weight: 700; }
.trace-table { min-width: 950px; }
.summary-table { min-width: 380px; }
.cost-table { min-width: 340px; }
.type-chip { display: inline-block; font-size: .72rem; border-radius: 6px; padding: 2px 7px; font-weight: 700; }
.type-chip--rm { background: #e0f5ef; color: #08745b; }
.type-chip--pm { background: #e8e9fb; color: #4246a1; }
.signature-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 38px; text-align: center; }
.signature-row > div { border-top: 1px solid #8d99a5; padding-top: 8px; font-size: .83rem; }
@media (max-width: 700px) {
  .heading-actions { position: static; margin-left: auto; }
  .document-heading { flex-wrap: wrap; }
  .section-body { padding: 10px; }
  .signature-row { gap: 10px; }
}
/* Print is a purpose-built body-level report; the on-screen Vuetify document is hidden. */
.production-print-sheet { display: none; }
@media print {
  @page { size: A4 landscape; margin: 9mm 10mm; }
  :global(body.remex-production-print-page) { background: #fff !important; margin: 0 !important; }
  :global(body.remex-production-print-page > #app) { display: none !important; }
  :global(body.remex-production-print-page > .production-print-sheet) {
    display: block !important;
    position: static !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9px;
    line-height: 1.25;
    color: #171c22;
    background: white;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .production-print-sheet, .production-print-sheet * { box-sizing: border-box; }
  .print-product-block { width: 100%; }
  .print-product-block + .print-product-block { break-before: page; page-break-before: always; }
  .print-heading { text-align: center; margin-bottom: 7px; }
  .print-heading h1 { font-size: 15px; font-weight: 700; letter-spacing: .15px; margin: 0 0 3px; }
  .print-heading h2 { font-size: 10px; text-decoration: underline; letter-spacing: .8px; margin: 0 0 3px; }
  .print-doc-code { font-size: 9px; color: #333; }
  .print-doc-code span { padding: 0 6px; }
  .print-meta { width: 100%; border-collapse: separate; border-spacing: 3px 0; margin-bottom: 8px; table-layout: fixed; }
  .print-meta td { border: 1px solid #c9d0d5; border-radius: 3px; padding: 5px 7px; background: #fff; vertical-align: top; }
  .print-meta span { display: block; color: #52606e; font-size: 8px; margin-bottom: 2px; }
  .print-meta strong { display: block; font-size: 9px; font-weight: 700; overflow-wrap: anywhere; }
  .print-product-title { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding: 6px 8px; border-top: 1px solid #7b8c99; border-bottom: 1px solid #7b8c99; background: #edf2f5; margin: 4px 0 7px; }
  .print-product-title strong { font-size: 11px; }
  .print-product-title span { font-size: 9px; }
  .print-meta--batch { margin-bottom: 6px; }
  .print-section-heading { font-size: 9px; font-weight: 700; background: #e9edef; padding: 4px 7px; margin: 8px 0 2px; border-left: 3px solid #657787; break-after: avoid; page-break-after: avoid; }
  .print-table { border-collapse: collapse; width: 100%; font-size: 8px; table-layout: auto; }
  .print-table th, .print-table td { border: 1px solid #bbc5ce; padding: 3px 5px; vertical-align: top; overflow-wrap: anywhere; }
  .print-table th { background: #e8edef; font-weight: 700; text-align: left; }
  .print-table thead { display: table-header-group; }
  .print-table tr { break-inside: avoid; page-break-inside: avoid; }
  .print-table .print-num { text-align: right; white-space: nowrap; }
  .print-total-row td { background: #f1f4f6; font-weight: 700; }
  .print-trace-table th:first-child { width: 20%; }
  .print-trace-table th:nth-child(3) { width: 17%; }
  .print-cost-table th { text-align: center; }
  .print-cost-table td { text-align: center; font-size: 9px; }
  .print-remarks { margin-top: 7px; padding: 4px 6px; border: 1px solid #d7dee4; }
  .print-signatures { display: grid; grid-template-columns: repeat(3, 1fr); gap: 42px; text-align: center; margin-top: 28px; break-inside: avoid; page-break-inside: avoid; }
  .print-signatures div { border-top: 1px solid #6d7c8a; padding-top: 5px; font-size: 9px; }
  .print-footnote { text-align: center; font-size: 7px; color: #66727d; padding-top: 10px; }
}
</style>
